import React, { useState, useEffect } from "react";
import OSIcon32 from "../icons/OSIcon32";
import "../../styles/App.css";

const bootMessages = [
  "Booting NeighborhoodOS v3.1...",
  "Reticulating splines...",
  "Raising capital...",
  "Tightening up pitch decks...",
  "Executing good-vibes.exe..."
];

// Per-variant presentation. Boot behavior (progress math, 50% preload signal,
// 100% completion signal) is shared; the variants differ in bypass interaction
// (desktop = any key, mobile = tap anywhere) and sizing.
const VARIANTS = {
  desktop: {
    screenStyle: undefined,
    boxWidth: 'min(440px, calc(100vw - 32px))',
    logoContainer: { width: 'auto', height: 'auto', margin: '0 auto 24px' },
    logoWidth: '180px',
    versionLabel: { fontSize: '11px', marginBottom: '6px' },
    heading: { fontSize: '28px', margin: '0 0 24px' },
    loadingRow: { gap: '12px' },
    loadingFontSize: '12px',
    progressBlocks: 22,
    skipHint: { marginTop: '22px' },
    skipText: 'Press any key to skip'
  },
  mobile: {
    screenStyle: {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      cursor: 'default'
    },
    boxWidth: 'min(85%, 360px)',
    logoContainer: { margin: '0 auto 18px' },
    logoWidth: '140px',
    versionLabel: { fontSize: '10px', marginBottom: '4px' },
    heading: { fontSize: '22px', margin: '0 0 20px 0' },
    loadingRow: { width: '100%', gap: '10px' },
    loadingFontSize: '11px',
    progressBlocks: 16,
    skipHint: { marginTop: '18px' },
    skipText: 'Tap anywhere to skip'
  }
};

export default function BootScreen({ onFinish, variant = "desktop" }) {
  const isMobile = variant === "mobile";
  const v = VARIANTS[isMobile ? "mobile" : "desktop"];
  const [messageIndex, setMessageIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [desktopPreloaded, setDesktopPreloaded] = useState(false);

  // Desktop variant: handle any-key bypass
  useEffect(() => {
    if (isMobile) return undefined;

    const handleKeyPress = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      e.preventDefault();
      if (onFinish) {
        onFinish(false); // Skip directly to desktop
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isMobile, onFinish]);

  // Mobile variant: handle click/touch bypass
  const handleBypass = () => {
    if (onFinish) {
      onFinish(false); // Skip directly to mobile landing
    }
  };

  // Handle preloading desktop components
  useEffect(() => {
    if (progress >= 50 && !desktopPreloaded) {
      setDesktopPreloaded(true);
      if (onFinish) {
        onFinish(true); // true = preload mode
      }
    }
  }, [progress, desktopPreloaded, onFinish]);

  // Progress bar animation and boot messages
  useEffect(() => {
    if (progress >= 100) return;

    const timer = setTimeout(() => {
      const increment = Math.random() * 2.5 + 2.5;
      const newProgress = Math.min(progress + increment, 100);
      setProgress(newProgress);

      const newIndex = Math.min(
        Math.floor((newProgress / 100) * bootMessages.length),
        bootMessages.length - 1
      );
      setMessageIndex(newIndex);

      // Signal boot completion
      if (newProgress >= 100 && onFinish) {
        onFinish(false); // false = final boot complete
      }
    }, 110);

    return () => clearTimeout(timer);
  }, [progress, onFinish]);

  const filledBlocks = Math.round((progress / 100) * v.progressBlocks);

  return (
    <div
      className="boot-screen"
      {...(isMobile
        ? { onClick: handleBypass, onTouchStart: handleBypass, style: v.screenStyle }
        : {})}
    >
      {/* Boot dialog — a chrome window: gradient titlebar over a raised frame */}
      <div className="boot-box" style={{
        width: v.boxWidth,
        padding: 0,
        background: 'var(--chrome-face)',
        border: '2px solid',
        borderColor: 'var(--bevel-raised)',
        boxShadow: 'var(--shadow-raised), var(--window-drop)',
        textAlign: 'center'
      }}>
        <div className="win95-titlebar" style={{ cursor: 'default' }}>
          <OSIcon32 kind="computer" size={15} className="win95-titlebar__icon" />
          <span className="win95-titlebar__label" style={{ textAlign: 'left' }}>
            NeighborhoodOS — starting up
          </span>
        </div>

        <div style={{ padding: isMobile ? '26px 22px 20px' : '36px 36px 26px' }}>
          <div className="logo-container" style={v.logoContainer}>
            <img
              src="/assets/gnf-logo.png"
              alt="gnf"
              className="boot-logo"
              style={{
                width: v.logoWidth,
                height: 'auto',
                display: 'block',
                margin: '0 auto'
              }}
            />
          </div>
          <div style={{
            fontFamily: 'var(--font-chrome)',
            fontWeight: 700,
            letterSpacing: 'var(--track-label)',
            color: 'var(--mb-magenta)',
            ...v.versionLabel
          }}>
            NeighborhoodOS v3.2
          </div>
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 700,
            letterSpacing: 'var(--track-head)',
            color: 'var(--chrome-outline)',
            ...v.heading
          }}>
            Booting up<span className="mb-boot-dots" aria-hidden="true" />
          </h1>

          <div style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            marginBottom: '8px',
            ...v.loadingRow
          }}>
            <p className="boot-loading-text" style={{
              fontFamily: 'var(--font-numeral)',
              fontSize: v.loadingFontSize,
              color: 'var(--chrome-text-soft)',
              margin: 0,
              textAlign: 'left',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              flex: 1
            }}>
              {bootMessages[messageIndex]}
            </p>
            <span style={{
              fontFamily: 'var(--font-numeral)',
              fontSize: v.loadingFontSize,
              color: 'var(--chrome-outline)',
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums'
            }}>
              {Math.floor(progress)}%
            </span>
          </div>

          {/* Segmented chrome progress — sunken track, magenta blocks */}
          <div
            className="win95-progress"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.floor(progress)}
            aria-label="Boot progress"
          >
            {Array.from({ length: v.progressBlocks }, (_, i) => (
              <span key={i} className={`win95-progress__block ${i < filledBlocks ? 'is-filled' : ''}`} />
            ))}
          </div>

          <p style={{
            fontSize: '10px',
            fontWeight: 600,
            color: 'var(--chrome-text-soft)',
            marginBottom: '0',
            fontFamily: 'var(--font-chrome)',
            letterSpacing: 'var(--track-label)',
            ...v.skipHint
          }}>
            {v.skipText}
          </p>
        </div>
      </div>
    </div>
  );
}
